// ---------------------------------------------------------------------------
// Analytics: turns the practice_attempt history into dashboard metrics, weak-area
// detection, and a readiness estimate. All deterministic / code-driven.
// ---------------------------------------------------------------------------

import type { InterviewQuestion, ScoredAttempt } from '../types';
import * as repo from '../db/repository';
import { getRubric } from '../ai/rubric';

export interface CategoryScore {
  category: string;
  averageScore: number;
  attempts: number;
}

export interface TrendPoint {
  index: number;
  date: string;
  score: number;
  rolling: number;
}

export interface CriterionAverage {
  key: string;
  label: string;
  average: number; // 1..5
}

export interface DashboardData {
  totalAttempts: number;
  questionsPracticed: number;
  totalQuestions: number;
  averageScore: number;
  bestScore: number;
  readiness: number;
  readinessLabel: string;
  categoryScores: CategoryScore[];
  scoreBySchool: { school: string; average: number; attempts: number }[];
  trend: TrendPoint[];
  weakCategories: CategoryScore[];
  criterionAverages: CriterionAverage[];
  weaknessInsights: string[];
  mockInterviewCount: number;
}

function avg(nums: number[]): number {
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
}

export function buildDashboard(): DashboardData {
  const attempts = repo.listAttempts();
  const questions = repo.listQuestions({ includeHidden: true });
  const questionById = new Map<string, InterviewQuestion>(questions.map((q) => [q.id, q]));
  const programs = repo.listPrograms();
  const programById = new Map(programs.map((p) => [p.id, p]));
  const mocks = repo.listMockInterviews();

  const scores = attempts.map((a) => a.finalScore);
  const averageScore = Math.round(avg(scores));
  const bestScore = scores.length ? Math.max(...scores) : 0;

  // Category averages.
  const byCategory = new Map<string, number[]>();
  const bySchool = new Map<string, number[]>();
  for (const a of attempts) {
    const q = questionById.get(a.questionId);
    const cat = q?.category ?? 'Unknown';
    (byCategory.get(cat) ?? byCategory.set(cat, []).get(cat)!).push(a.finalScore);
    if (q?.programId) {
      const school = programById.get(q.programId)?.shortName ?? 'Program';
      (bySchool.get(school) ?? bySchool.set(school, []).get(school)!).push(a.finalScore);
    }
  }
  const categoryScores: CategoryScore[] = Array.from(byCategory.entries())
    .map(([category, arr]) => ({ category, averageScore: Math.round(avg(arr)), attempts: arr.length }))
    .sort((a, b) => b.attempts - a.attempts);

  const scoreBySchool = Array.from(bySchool.entries()).map(([school, arr]) => ({
    school,
    average: Math.round(avg(arr)),
    attempts: arr.length,
  }));

  // Trend with a rolling average (window 3).
  const trend: TrendPoint[] = attempts.map((a, i) => {
    const window = attempts.slice(Math.max(0, i - 2), i + 1).map((x) => x.finalScore);
    return {
      index: i + 1,
      date: a.createdAt.slice(0, 10),
      score: a.finalScore,
      rolling: Math.round(avg(window)),
    };
  });

  // Criterion averages across all attempts.
  const rubric = getRubric(attempts[0]?.rubricVersion ?? 'dental-interview-v1.0');
  const criterionAverages: CriterionAverage[] = rubric.criteria.map((c) => ({
    key: c.key,
    label: c.label,
    average: Number(avg(attempts.map((a) => a.scores[c.key] ?? 0)).toFixed(2)),
  }));

  const weakCategories = [...categoryScores]
    .filter((c) => c.attempts > 0)
    .sort((a, b) => a.averageScore - b.averageScore)
    .slice(0, 3);

  // Readiness estimate: blend of average score, breadth of practice, and consistency.
  const breadth = questions.length ? Math.min(1, attempts.length / Math.max(8, questions.length * 0.5)) : 0;
  const consistency = scores.length > 1 ? 1 - Math.min(1, stdDev(scores) / 30) : 0.5;
  const readiness = Math.round(
    Math.min(100, averageScore * 0.7 + breadth * 20 + consistency * 10),
  );
  const readinessLabel =
    readiness >= 80 ? 'Interview Ready' : readiness >= 60 ? 'On Track' : readiness >= 40 ? 'Developing' : 'Getting Started';

  const weaknessInsights = detectWeaknesses(attempts, criterionAverages);

  return {
    totalAttempts: attempts.length,
    questionsPracticed: questions.filter((q) => q.practiced).length,
    totalQuestions: questions.filter((q) => !q.hidden).length,
    averageScore,
    bestScore,
    readiness: attempts.length ? readiness : 0,
    readinessLabel: attempts.length ? readinessLabel : 'Getting Started',
    categoryScores,
    scoreBySchool,
    trend,
    weakCategories,
    criterionAverages,
    weaknessInsights,
    mockInterviewCount: mocks.filter((m) => m.status === 'completed').length,
  };
}

function stdDev(nums: number[]): number {
  const m = avg(nums);
  return Math.sqrt(avg(nums.map((n) => (n - m) ** 2)));
}

export function detectWeaknesses(
  attempts: ScoredAttempt[],
  criterionAverages: CriterionAverage[],
): string[] {
  if (attempts.length < 2) return ['Practice a few more questions to unlock pattern detection.'];
  const insights: string[] = [];
  const n = attempts.length;

  const flagCounts: Record<string, number> = {};
  for (const a of attempts) for (const f of a.grading.flags ?? []) flagCounts[f] = (flagCounts[f] ?? 0) + 1;

  if ((flagCounts.too_generic ?? 0) / n > 0.4) insights.push('You often answer too generally — add concrete examples.');
  if ((flagCounts.missing_school_fit ?? 0) / n > 0.4) insights.push('You need stronger school-specific reasons.');
  if ((flagCounts.filler_heavy ?? 0) / n > 0.4) insights.push('You use too many filler words.');
  if ((flagCounts.weak_dental_motivation ?? 0) / n > 0.4) insights.push('You rarely connect answers back to dentistry.');
  if ((flagCounts.missing_reflection ?? 0) / n > 0.4) insights.push('Your answers miss what you learned from the experience.');

  // Lowest criterion.
  const weakest = [...criterionAverages].sort((a, b) => a.average - b.average)[0];
  if (weakest && weakest.average < 3.5) insights.push(`Your weakest rubric area is "${weakest.label}" (avg ${weakest.average}/5).`);

  if (!insights.length) insights.push('No dominant weakness detected — your answers are consistent. Keep sharpening specifics.');
  return insights;
}
